/**
 * supportCaseVocabulary
 * ---------------------------------------------------------------------------
 * ONE source of truth for every support case enum, human label and pure
 * derivation the dashboard needs. Nothing else in the SPA may declare a case
 * status, category, priority, team, resolution outcome or assignee type: the
 * queue view, the detail panel and the api module all read from here.
 *
 * Live vocabulary comes from GET /api/support-cases/meta/options. The lists
 * below are the SHIPPED FALLBACK for the moment that call has not answered yet
 * or fails outright, so the queue still renders usable filters instead of an
 * empty dropdown. `mergeCaseVocabulary` layers the live payload over them.
 *
 * The human status labels are fixed by the build contract and must not be
 * reworded here. Everything else falls back to `humanizeEnum` so a value the
 * server adds later still renders as readable text rather than SCREAMING_SNAKE.
 *
 * Every function in this file is pure. Nothing here touches the network, the
 * socket, localStorage or React.
 */

// --- Statuses -------------------------------------------------------------
export const CASE_STATUSES = [
  "OPEN",
  "ACKNOWLEDGED",
  "IN_PROGRESS",
  "PENDING_CUSTOMER",
  "ESCALATED",
  "RESOLVED",
  "CLOSED",
  "CANCELLED",
];

/** Fixed by the build contract. Do not reword. */
export const CASE_STATUS_LABELS = {
  OPEN: "Open",
  ACKNOWLEDGED: "Acknowledged",
  IN_PROGRESS: "In progress",
  PENDING_CUSTOMER: "Waiting on you",
  ESCALATED: "Escalated",
  RESOLVED: "Resolved",
  CLOSED: "Closed",
  CANCELLED: "Cancelled",
};

/** Chip tone keys. The CSS owns the colours, this owns the meaning. */
export const CASE_STATUS_TONES = {
  OPEN: "info",
  ACKNOWLEDGED: "info",
  IN_PROGRESS: "progress",
  PENDING_CUSTOMER: "waiting",
  ESCALATED: "urgent",
  RESOLVED: "good",
  CLOSED: "muted",
  CANCELLED: "muted",
};

/** Statuses that mean the case is off the active queue. */
export const TERMINAL_CASE_STATUSES = ["RESOLVED", "CLOSED", "CANCELLED"];

// --- Categories -----------------------------------------------------------
export const CASE_CATEGORIES = [
  "ORDER_ISSUE",
  "DELIVERY",
  "PAYMENT_REFUND",
  "PRODUCT_QUALITY",
  "MISSING_ITEM",
  "WRONG_ITEM",
  "VENDOR_SERVICE",
  "RIDER_CONDUCT",
  "APP_TECHNICAL",
  "ACCOUNT",
  "OTHER",
];

export const CASE_CATEGORY_LABELS = {
  ORDER_ISSUE: "Order issue",
  DELIVERY: "Delivery",
  PAYMENT_REFUND: "Payment or refund",
  PRODUCT_QUALITY: "Product quality",
  MISSING_ITEM: "Missing item",
  WRONG_ITEM: "Wrong item",
  VENDOR_SERVICE: "Vendor service",
  RIDER_CONDUCT: "Rider conduct",
  APP_TECHNICAL: "App or technical",
  ACCOUNT: "Account",
  OTHER: "Other",
};

// --- Priorities -----------------------------------------------------------
export const CASE_PRIORITIES = ["LOW", "NORMAL", "HIGH", "URGENT"];

export const CASE_PRIORITY_LABELS = {
  LOW: "Low",
  NORMAL: "Normal",
  HIGH: "High",
  URGENT: "Urgent",
};

export const CASE_PRIORITY_TONES = {
  LOW: "muted",
  NORMAL: "info",
  HIGH: "warn",
  URGENT: "urgent",
};

/** Highest number is the most pressing. Used for ordering, never for policy. */
export const CASE_PRIORITY_RANK = { LOW: 1, NORMAL: 2, HIGH: 3, URGENT: 4 };

// --- Teams ----------------------------------------------------------------
export const CASE_TEAMS = ["SUPPORT", "OPS", "FINANCE", "TECH"];

export const CASE_TEAM_LABELS = {
  SUPPORT: "Support",
  OPS: "Operations",
  FINANCE: "Finance",
  TECH: "Technical",
};

// --- Assignment -----------------------------------------------------------
export const CASE_ASSIGNEE_TYPES = ["UNASSIGNED", "VENDOR", "STAFF"];

export const CASE_ASSIGNEE_TYPE_LABELS = {
  UNASSIGNED: "Unassigned",
  VENDOR: "Vendor",
  STAFF: "Lusuku staff",
};

// --- Resolution -----------------------------------------------------------
export const CASE_RESOLUTION_OUTCOMES = [
  "RESOLVED_NO_ACTION",
  "REFUND_ISSUED",
  "PARTIAL_REFUND",
  "REPLACEMENT_SENT",
  "CREDIT_ISSUED",
  "ESCALATED_EXTERNAL",
  "DUPLICATE",
  "INVALID",
];

export const CASE_RESOLUTION_OUTCOME_LABELS = {
  RESOLVED_NO_ACTION: "Resolved, no action needed",
  REFUND_ISSUED: "Refund issued",
  PARTIAL_REFUND: "Partial refund",
  REPLACEMENT_SENT: "Replacement sent",
  CREDIT_ISSUED: "Credit issued",
  ESCALATED_EXTERNAL: "Escalated externally",
  DUPLICATE: "Duplicate",
  INVALID: "Invalid",
};

/** Outcomes that normally carry money, so the composer shows the amount field. */
export const FINANCIAL_RESOLUTION_OUTCOMES = [
  "REFUND_ISSUED",
  "PARTIAL_REFUND",
  "CREDIT_ISSUED",
];

// --- Author roles ---------------------------------------------------------
export const CASE_AUTHOR_ROLE_LABELS = {
  CUSTOMER: "Customer",
  VENDOR: "Vendor",
  VENDOR_MANAGER: "Vendor manager",
  STAFF: "Lusuku staff",
  RIDER: "Rider",
};

// --- Sorting --------------------------------------------------------------
export const CASE_SORTS = ["-createdAt", "-lastActivityAt", "priority"];

export const CASE_SORT_LABELS = {
  "-createdAt": "Newest first",
  "-lastActivityAt": "Recent activity",
  priority: "Priority",
};

export const DEFAULT_CASE_SORT = "-createdAt";
export const DEFAULT_CASE_PAGE_SIZE = 20;
export const MAX_CASE_PAGE_SIZE = 100;

// --- Label helpers --------------------------------------------------------

/**
 * SCREAMING_SNAKE to sentence case, so an enum value the server adds after this
 * build still reads as words. "PAYMENT_REFUND" becomes "Payment refund".
 */
export const humanizeEnum = (value) => {
  const raw = value == null ? "" : String(value).trim();
  if (!raw) return "";
  const words = raw.replace(/[_-]+/g, " ").toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return "";
  return words
    .map((word, index) => (index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ");
};

const lookup = (map, value, fallback = "") => {
  const key = value == null ? "" : String(value);
  if (!key) return fallback;
  return map[key] || humanizeEnum(key);
};

export const caseStatusLabel = (value) => lookup(CASE_STATUS_LABELS, value, "Unknown");
export const caseCategoryLabel = (value) => lookup(CASE_CATEGORY_LABELS, value, "Uncategorised");
export const casePriorityLabel = (value) => lookup(CASE_PRIORITY_LABELS, value, "Normal");
export const caseTeamLabel = (value) => lookup(CASE_TEAM_LABELS, value, "");
export const caseAssigneeTypeLabel = (value) =>
  lookup(CASE_ASSIGNEE_TYPE_LABELS, value, "Unassigned");
export const caseResolutionOutcomeLabel = (value) =>
  lookup(CASE_RESOLUTION_OUTCOME_LABELS, value, "");
export const caseAuthorRoleLabel = (value) => lookup(CASE_AUTHOR_ROLE_LABELS, value, "");
export const caseSortLabel = (value) => lookup(CASE_SORT_LABELS, value, "");

export const caseStatusTone = (value) => CASE_STATUS_TONES[String(value || "")] || "muted";
export const casePriorityTone = (value) => CASE_PRIORITY_TONES[String(value || "")] || "muted";
export const casePriorityRank = (value) => CASE_PRIORITY_RANK[String(value || "")] || 0;

export const isTerminalCaseStatus = (value) =>
  TERMINAL_CASE_STATUSES.includes(String(value || ""));

export const isFinancialResolutionOutcome = (value) =>
  FINANCIAL_RESOLUTION_OUTCOMES.includes(String(value || ""));

/** Turn a list of enum values into [{ value, label }] for a select. */
export const toCaseOptions = (values, labelFn) =>
  (Array.isArray(values) ? values : []).map((value) => ({
    value,
    label: typeof labelFn === "function" ? labelFn(value) : humanizeEnum(value),
  }));

// --- Live vocabulary ------------------------------------------------------

/**
 * What the queue uses when GET /meta/options has not answered. Shipped so a
 * cold start or a failed call never leaves the filter bar empty.
 */
export const SHIPPED_CASE_VOCABULARY = {
  statuses: CASE_STATUSES,
  categories: CASE_CATEGORIES,
  priorities: CASE_PRIORITIES,
  teams: CASE_TEAMS,
  assigneeTypes: CASE_ASSIGNEE_TYPES,
  resolutionOutcomes: CASE_RESOLUTION_OUTCOMES,
};

/**
 * The options payload may arrive bare, under `data`, or under `options`, and
 * each list may be an array of strings or of { value, label } objects. Pull out
 * plain string arrays and drop anything unusable rather than guessing.
 */
const readOptionList = (raw) => {
  if (!Array.isArray(raw)) return null;
  const values = raw
    .map((entry) => {
      if (typeof entry === "string") return entry.trim();
      if (entry && typeof entry === "object") {
        return String(entry.value ?? entry.key ?? entry.name ?? "").trim();
      }
      return "";
    })
    .filter(Boolean);
  return values.length ? Array.from(new Set(values)) : null;
};

const OPTION_ALIASES = {
  statuses: ["statuses", "status", "caseStatuses", "CASE_STATUSES"],
  categories: ["categories", "category", "caseCategories", "CASE_CATEGORIES"],
  priorities: ["priorities", "priority", "casePriorities", "CASE_PRIORITIES"],
  teams: ["teams", "team", "caseTeams", "CASE_TEAMS"],
  assigneeTypes: ["assigneeTypes", "assigneeType", "CASE_ASSIGNEE_TYPES"],
  resolutionOutcomes: [
    "resolutionOutcomes",
    "outcomes",
    "resolutionOutcome",
    "RESOLUTION_OUTCOMES",
  ],
};

/** Unwrap whatever envelope the options endpoint uses. */
export const normalizeMetaOptions = (payload) => {
  const roots = [payload, payload?.data, payload?.options, payload?.data?.options].filter(
    (candidate) => candidate && typeof candidate === "object"
  );
  const out = {};
  Object.keys(OPTION_ALIASES).forEach((field) => {
    for (const root of roots) {
      for (const alias of OPTION_ALIASES[field]) {
        const values = readOptionList(root[alias]);
        if (values) {
          out[field] = values;
          return;
        }
      }
    }
  });
  return out;
};

/**
 * Live lists win where present, the shipped lists fill every gap. Never returns
 * an empty list for a field the queue renders a dropdown from.
 */
export const mergeCaseVocabulary = (livePayload) => {
  const live = normalizeMetaOptions(livePayload);
  const merged = { ...SHIPPED_CASE_VOCABULARY };
  Object.keys(SHIPPED_CASE_VOCABULARY).forEach((field) => {
    if (Array.isArray(live[field]) && live[field].length) merged[field] = live[field];
  });
  return merged;
};

// --- SLA ------------------------------------------------------------------

/**
 * "3h 5m", "2d 4h", "45m", "Less than a minute". Always a positive duration:
 * the caller decides whether it reads as remaining or overdue.
 */
export const formatCaseDuration = (ms) => {
  const total = Math.abs(Number(ms));
  if (!Number.isFinite(total)) return "";
  const minutes = Math.floor(total / 60000);
  if (minutes < 1) return "Less than a minute";
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  return `${mins}m`;
};

const toMillis = (value) => {
  if (!value) return null;
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
};

/**
 * The single SLA read the row and the detail header both use. Driven by
 * `sla.breachedFirstResponse` and `sla.breachedResolution`, which the server
 * owns: this never decides a breach by comparing clocks, it only describes how
 * much time is left on whichever clock is still running.
 *
 * Degrades to `{ known: false }` when the case carries no `sla` at all, which is
 * what a scoped caller may legitimately receive.
 */
export const describeCaseSla = (supportCase, now = Date.now()) => {
  const sla = supportCase?.sla || {};
  const firstResponseDueAt = sla.firstResponseDueAt || null;
  const resolutionDueAt = sla.resolutionDueAt || null;
  const responded = Boolean(sla.firstRespondedAt);
  const settled = Boolean(sla.resolvedAt);
  const breachedFirstResponse = sla.breachedFirstResponse === true;
  const breachedResolution = sla.breachedResolution === true;
  const breached = breachedFirstResponse || breachedResolution;

  let breachLabel = "";
  if (breachedFirstResponse && breachedResolution) breachLabel = "First response and resolution breached";
  else if (breachedFirstResponse) breachLabel = "First response breached";
  else if (breachedResolution) breachLabel = "Resolution breached";

  if (!firstResponseDueAt && !resolutionDueAt) {
    return {
      known: false,
      breached,
      breachedFirstResponse,
      breachedResolution,
      breachLabel,
      dueAt: null,
      dueKind: null,
      msRemaining: null,
      overdue: false,
      tone: breached ? "urgent" : "muted",
      label: breached ? breachLabel : "No target set",
    };
  }

  let dueAt = null;
  let dueKind = null;
  if (!responded && firstResponseDueAt) {
    dueAt = firstResponseDueAt;
    dueKind = "firstResponse";
  } else if (resolutionDueAt) {
    dueAt = resolutionDueAt;
    dueKind = "resolution";
  } else {
    dueAt = firstResponseDueAt;
    dueKind = "firstResponse";
  }

  const dueMs = toMillis(dueAt);
  const nowMs = toMillis(now) ?? Date.now();
  const msRemaining = dueMs == null ? null : dueMs - nowMs;
  const overdue = msRemaining != null && msRemaining < 0;
  const clockName = dueKind === "firstResponse" ? "first response" : "resolution";

  let label;
  let tone;
  if (breached) {
    label = breachLabel;
    tone = "urgent";
  } else if (settled) {
    label = "Closed within target";
    tone = "good";
  } else if (overdue) {
    label = `${humanizeEnum(clockName)} overdue by ${formatCaseDuration(msRemaining)}`;
    tone = "warn";
  } else {
    label = `${humanizeEnum(clockName)} due in ${formatCaseDuration(msRemaining)}`;
    tone = msRemaining != null && msRemaining < 60 * 60 * 1000 ? "warn" : "good";
  }

  return {
    known: true,
    breached,
    breachedFirstResponse,
    breachedResolution,
    breachLabel,
    dueAt,
    dueKind,
    msRemaining,
    overdue,
    tone,
    label,
  };
};

// --- List query -----------------------------------------------------------

/** Exactly the list query params the contract names. Nothing else is sent. */
export const CASE_LIST_FILTER_KEYS = [
  "status",
  "category",
  "priority",
  "assigneeType",
  "assignedTo",
  "vendorId",
  "orderId",
  "breached",
  "q",
  "from",
  "to",
  "page",
  "limit",
  "sort",
];

export const EMPTY_CASE_FILTERS = {
  status: "",
  category: "",
  priority: "",
  assigneeType: "",
  assignedTo: "",
  vendorId: "",
  orderId: "",
  breached: false,
  q: "",
  from: "",
  to: "",
  page: 1,
  limit: DEFAULT_CASE_PAGE_SIZE,
  sort: DEFAULT_CASE_SORT,
};

const clampPage = (value) => {
  const page = Math.floor(Number(value));
  return Number.isFinite(page) && page > 0 ? page : 1;
};

const clampLimit = (value) => {
  const limit = Math.floor(Number(value));
  if (!Number.isFinite(limit) || limit <= 0) return DEFAULT_CASE_PAGE_SIZE;
  return Math.min(limit, MAX_CASE_PAGE_SIZE);
};

/**
 * Filter state to request params: drops every blank, clamps the page and the
 * limit, sends `breached` only when it is actually on, and refuses a sort the
 * contract does not list.
 */
export const buildCaseListQuery = (filters = {}) => {
  const params = {};
  ["status", "category", "priority", "assigneeType", "assignedTo", "vendorId", "orderId", "from", "to"].forEach(
    (key) => {
      const value = filters[key];
      if (value == null) return;
      const trimmed = String(value).trim();
      if (trimmed) params[key] = trimmed;
    }
  );
  const q = filters.q == null ? "" : String(filters.q).trim();
  if (q) params.q = q;
  if (filters.breached === true || filters.breached === "true") params.breached = "true";
  params.page = clampPage(filters.page);
  params.limit = clampLimit(filters.limit);
  params.sort = CASE_SORTS.includes(filters.sort) ? filters.sort : DEFAULT_CASE_SORT;
  return params;
};

/** True when anything narrows the queue, so the view can offer a reset. */
export const hasActiveCaseFilters = (filters = {}) => {
  const query = buildCaseListQuery(filters);
  return Object.keys(query).some((key) => !["page", "limit", "sort"].includes(key));
};

// --- Create draft ---------------------------------------------------------

/**
 * The server's own limits on a new case, from the frozen contract: subject max
 * 140, description max 4000. Declared here so the create form validates against
 * the SAME numbers the model enforces instead of its own copy. These are a
 * courtesy check only: the server still refuses an over-long field with
 * SUPPORT_CASE_INVALID_PAYLOAD and that refusal is what the form reports.
 */
export const SUPPORT_CASE_SUBJECT_MAX = 140;
export const SUPPORT_CASE_DESCRIPTION_MAX = 4000;

/** How many evidence links one create call may carry. A client-side sanity cap. */
export const MAX_CASE_EVIDENCE_ITEMS = 10;

/** The evidence `type` values the model stores. */
export const CASE_EVIDENCE_TYPES = ["image", "video", "document", "other"];

/**
 * A blank create draft. `priority` and `isIncident` exist on the draft for every
 * caller but are only ever SENT by a staff caller: the server derives a priority
 * when none arrives, and an incident is a platform-wide call that a vendor
 * manager does not make. buildCreateCasePayload drops both for a non-staff
 * caller rather than trusting the form to have hidden them.
 */
export const EMPTY_CASE_DRAFT = {
  subject: "",
  description: "",
  category: "",
  priority: "",
  orderId: "",
  vendorId: "",
  isIncident: false,
  evidenceUrls: "",
};

const EVIDENCE_IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif|avif)$/i;
const EVIDENCE_VIDEO_EXT = /\.(mp4|mov|m4v|webm|avi|mkv|3gp)$/i;
const EVIDENCE_DOC_EXT = /\.(pdf|docx?|xlsx?|pptx?|csv|txt|rtf)$/i;

/**
 * The evidence `type` for a url, read from its extension with the query string
 * and the fragment stripped first. Anything unrecognised is "other" rather than
 * a guess, so a signed link with no extension still uploads cleanly.
 */
export const caseEvidenceTypeForUrl = (url) => {
  const raw = url == null ? "" : String(url).trim();
  if (!raw) return "other";
  const path = raw.split(/[?#]/)[0];
  if (EVIDENCE_IMAGE_EXT.test(path)) return "image";
  if (EVIDENCE_VIDEO_EXT.test(path)) return "video";
  if (EVIDENCE_DOC_EXT.test(path)) return "document";
  return "other";
};

const looksLikeHttpUrl = (value) => /^https?:\/\/[^\s]+$/i.test(value);

/**
 * A pasted block of links to `evidence: [{ url, type }]`. Splits on newlines,
 * commas and whitespace, drops duplicates, keeps the original order, and returns
 * whatever did NOT look like a web address separately so the form can name it
 * instead of silently discarding it.
 */
export const parseEvidenceUrls = (text) => {
  const tokens = String(text == null ? "" : text)
    .split(/[\s,]+/)
    .map((token) => token.trim())
    .filter(Boolean);
  const seen = new Set();
  const evidence = [];
  const invalid = [];
  tokens.forEach((token) => {
    if (!looksLikeHttpUrl(token)) {
      if (!invalid.includes(token)) invalid.push(token);
      return;
    }
    if (seen.has(token)) return;
    seen.add(token);
    evidence.push({ url: token, type: caseEvidenceTypeForUrl(token) });
  });
  return { evidence, invalid };
};

/**
 * Client-side validation of a create draft, matching the server's limits. Pure,
 * and deliberately NOT a permission decision: `staff` is passed in by the view
 * that already decided it, and all this does is refuse to send a staff-only
 * field from a caller who is not staff.
 *
 * `options.categories` and `options.priorities` accept the LIVE vocabulary, so a
 * category the server added after this build is accepted rather than rejected
 * against a stale shipped list.
 */
export const validateCaseDraft = (draft = {}, options = {}) => {
  const staff = options.staff === true;
  const categories =
    Array.isArray(options.categories) && options.categories.length
      ? options.categories
      : CASE_CATEGORIES;
  const priorities =
    Array.isArray(options.priorities) && options.priorities.length
      ? options.priorities
      : CASE_PRIORITIES;

  const errors = {};

  const subject = String(draft.subject == null ? "" : draft.subject).trim();
  if (!subject) errors.subject = "Give the case a subject.";
  else if (subject.length > SUPPORT_CASE_SUBJECT_MAX) {
    errors.subject = `Keep the subject to ${SUPPORT_CASE_SUBJECT_MAX} characters or fewer.`;
  }

  const description = String(draft.description == null ? "" : draft.description).trim();
  if (!description) errors.description = "Describe what happened.";
  else if (description.length > SUPPORT_CASE_DESCRIPTION_MAX) {
    errors.description = `Keep the description to ${SUPPORT_CASE_DESCRIPTION_MAX} characters or fewer.`;
  }

  const category = String(draft.category == null ? "" : draft.category).trim();
  if (!category) errors.category = "Choose a category.";
  else if (!categories.includes(category)) errors.category = "Choose a category from the list.";

  const priority = String(draft.priority == null ? "" : draft.priority).trim();
  if (priority) {
    if (!staff) errors.priority = "Only Lusuku staff set a priority.";
    else if (!priorities.includes(priority)) errors.priority = "Choose a priority from the list.";
  }

  if (draft.isIncident === true && !staff) {
    errors.isIncident = "Only Lusuku staff log a platform incident.";
  }

  const { evidence, invalid } = parseEvidenceUrls(draft.evidenceUrls);
  if (invalid.length) {
    errors.evidenceUrls = `Not a web address: ${invalid.join(", ")}`;
  } else if (evidence.length > MAX_CASE_EVIDENCE_ITEMS) {
    errors.evidenceUrls = `Attach at most ${MAX_CASE_EVIDENCE_ITEMS} links.`;
  }

  return { valid: Object.keys(errors).length === 0, errors };
};

/**
 * Draft to the create body the contract names:
 * `{ subject, description, category, orderId?, vendorId?, evidence? }`, plus
 * `priority?` and `isIncident?` which only a staff caller ever sends. A blank
 * optional field is omitted rather than sent empty, and a vendor manager never
 * sends `vendorId` at all: the server scopes them to their own vendor.
 */
export const buildCreateCasePayload = (draft = {}, options = {}) => {
  const staff = options.staff === true;
  const trimmed = (key) => String(draft[key] == null ? "" : draft[key]).trim();

  const payload = {
    subject: trimmed("subject"),
    description: trimmed("description"),
    category: trimmed("category"),
  };

  const orderId = trimmed("orderId");
  if (orderId) payload.orderId = orderId;

  if (staff) {
    const vendorId = trimmed("vendorId");
    if (vendorId) payload.vendorId = vendorId;
    const priority = trimmed("priority");
    if (priority) payload.priority = priority;
    if (draft.isIncident === true) payload.isIncident = true;
  }

  const { evidence } = parseEvidenceUrls(draft.evidenceUrls);
  if (evidence.length) payload.evidence = evidence;

  return payload;
};

/** The create-draft fields a server message can be pinned to, longest first. */
const CREATE_FIELD_PATTERNS = [
  ["description", /description/i],
  ["subject", /subject/i],
  ["category", /categor/i],
  ["priority", /priorit/i],
  ["evidenceUrls", /evidence/i],
  ["orderId", /\border\b/i],
  ["vendorId", /\bvendor\b/i],
];

/**
 * Which create field a server rejection is about, or "" when it names none.
 * Used to put a SUPPORT_CASE_INVALID_PAYLOAD message next to the field it is
 * complaining about as well as at the top of the form. It never invents a
 * field: an unrecognisable message stays a form-level error.
 */
export const caseCreateFieldFromMessage = (message) => {
  const text = message == null ? "" : String(message);
  if (!text.trim()) return "";
  for (const [field, pattern] of CREATE_FIELD_PATTERNS) {
    if (pattern.test(text)) return field;
  }
  return "";
};

// --- Response normalisers -------------------------------------------------

const firstArray = (...candidates) => {
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }
  return [];
};

const firstNumber = (...candidates) => {
  for (const candidate of candidates) {
    const n = Number(candidate);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

/**
 * The backend is built concurrently against the same contract, which fixes the
 * FIELDS on a case but not the envelope the repo's controllers wrap a list in
 * (some use `{ data, total, hasMore }`, others return the array directly). Read
 * both rather than white-screening on the one we did not expect.
 */
export const normalizeCaseListPayload = (payload) => {
  const cases = firstArray(
    payload,
    payload?.cases,
    payload?.data,
    payload?.items,
    payload?.results,
    payload?.data?.cases,
    payload?.data?.items,
    payload?.data?.data
  );
  const pagination = payload?.pagination || payload?.meta || payload?.data?.pagination || {};
  const total = firstNumber(payload?.total, payload?.count, pagination.total, pagination.count) ?? cases.length;
  const page = clampPage(firstNumber(payload?.page, pagination.page) ?? 1);
  const limit = clampLimit(firstNumber(payload?.limit, pagination.limit) ?? DEFAULT_CASE_PAGE_SIZE);
  const explicitHasMore = payload?.hasMore ?? pagination.hasMore;
  const hasMore =
    typeof explicitHasMore === "boolean" ? explicitHasMore : page * limit < total;
  return { cases, total, page, limit, hasMore };
};

/** One case, however the detail endpoint wraps it. */
export const normalizeCasePayload = (payload) => {
  const candidates = [
    payload?.case,
    payload?.supportCase,
    payload?.data?.case,
    payload?.data?.supportCase,
    payload?.data,
    payload,
  ];
  for (const candidate of candidates) {
    if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
      if (candidate._id || candidate.caseNumber) return candidate;
    }
  }
  return null;
};

const countMapFrom = (raw, keys) => {
  const out = {};
  keys.forEach((key) => {
    out[key] = 0;
  });
  if (Array.isArray(raw)) {
    raw.forEach((entry) => {
      const key = String(entry?._id ?? entry?.key ?? entry?.status ?? entry?.priority ?? "").trim();
      const count = Number(entry?.count ?? entry?.total ?? 0);
      if (key && Number.isFinite(count)) out[key] = (out[key] || 0) + count;
    });
    return out;
  }
  if (raw && typeof raw === "object") {
    Object.keys(raw).forEach((key) => {
      const count = Number(raw[key]);
      if (Number.isFinite(count)) out[key] = count;
    });
  }
  return out;
};

/** Summary strip input: counts by status, counts by priority, breach count. */
export const normalizeCaseSummaryPayload = (payload) => {
  const root = payload?.summary || payload?.data?.summary || payload?.data || payload || {};
  const byStatus = countMapFrom(root.byStatus ?? root.statuses ?? root.status, CASE_STATUSES);
  const byPriority = countMapFrom(
    root.byPriority ?? root.priorities ?? root.priority,
    CASE_PRIORITIES
  );
  const statusTotal = CASE_STATUSES.reduce((acc, key) => acc + (byStatus[key] || 0), 0);
  const total = firstNumber(root.total, root.count, statusTotal) ?? 0;
  const breached =
    firstNumber(
      root.breached,
      root.slaBreaches,
      root.breachedCount,
      root.sla?.breached,
      root.sla?.total
    ) ?? 0;
  const open = CASE_STATUSES.filter((status) => !isTerminalCaseStatus(status)).reduce(
    (acc, status) => acc + (byStatus[status] || 0),
    0
  );
  return { byStatus, byPriority, total, breached, open };
};

// --- Message thread -------------------------------------------------------

export const isInternalCaseNote = (message) => message?.isInternal === true;

/**
 * Split a thread into what the customer can see and what only the handling
 * side can. Both halves stay in their original chronological order, and the
 * combined `thread` is what the panel renders so a note sits where it happened.
 */
export const partitionCaseMessages = (messages) => {
  const list = Array.isArray(messages) ? messages : [];
  const publicReplies = list.filter((message) => !isInternalCaseNote(message));
  const internalNotes = list.filter(isInternalCaseNote);
  return { thread: list, publicReplies, internalNotes };
};

export default SHIPPED_CASE_VOCABULARY;
